/**
 * /form/$token — formulário externo PÚBLICO de abertura de solicitação
 * (sem AuthGate, fetch sem bearer — mesmo padrão do RatePage):
 * GET /public/form/:token mostra a mesa; nome + e-mail + título + descrição
 * (todos obrigatórios) + Enviar (POST). Estados: token inválido (aviso
 * "Formulário indisponível"), erro de rede e sucesso ("Recebemos sua
 * solicitação" — SEM número do ticket, evita enumeração).
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, CircleAlert, Send } from 'lucide-react';
import type { ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { PageSpinner, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { PublicBrandMark } from '@/shared/brand-mark';
import { errorMessage, publicFormQuery, usePublicFormSubmit } from './portal-queries';

/** Shell público centrado — mesmo padrão visual do /rate e do /kb. */
function FormShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <header className="flex flex-col items-center gap-1 pt-10 pb-8">
        <PublicBrandMark className="text-2xl text-sidebar" iconClassName="size-6" />
        <span className="text-sm text-muted-foreground">Abertura de solicitação</span>
      </header>
      <main className="mx-auto w-full max-w-lg px-4 pb-16">{children}</main>
    </div>
  );
}

function Notice({
  icon: Icon,
  tone,
  title,
  message,
}: {
  icon: typeof CircleAlert;
  tone: 'success' | 'muted';
  title: string;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border bg-card p-8 text-center shadow-xs">
      <Icon
        className={cn('size-10', tone === 'success' ? 'text-success' : 'text-muted-foreground/50')}
        strokeWidth={1.5}
        aria-hidden
      />
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

/** Validação simples de e-mail — só o formato grosseiro; o backend revalida. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Field {
  id: string;
  label: string;
  value: string;
  set: (v: string) => void;
}

export function FormPage() {
  // Rota /form/$token registrada pelo router (W1) — param não tipado aqui.
  const params = useParams({ strict: false }) as { token?: string };
  const token = params.token ?? '';
  const view = useQuery(publicFormQuery(token));
  const submitForm = usePublicFormSubmit(token);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (submitForm.isPending) return;
    setError(null);
    if (!name.trim() || !email.trim() || !title.trim() || !description.trim()) {
      setError('Preencha todos os campos.');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError('Informe um e-mail válido.');
      return;
    }
    submitForm.mutate(
      {
        name: name.trim(),
        email: email.trim(),
        title: title.trim(),
        description: description.trim(),
      },
      {
        onSuccess: () => setSent(true),
        onError: (err) => {
          const status = (err as unknown as ApiError).status;
          setError(
            status === 0
              ? 'Falha de conexão. Verifique sua internet e tente novamente.'
              : `Não foi possível enviar sua solicitação: ${errorMessage(err)}`
          );
        },
      }
    );
  };

  if (view.isPending) {
    return (
      <FormShell>
        <PageSpinner />
      </FormShell>
    );
  }

  if (view.isError) {
    const status = (view.error as unknown as ApiError).status;
    const invalid = status === 404 || status === 410 || status === 400;
    return (
      <FormShell>
        <Notice
          icon={CircleAlert}
          tone="muted"
          title={invalid ? 'Formulário indisponível' : 'Não foi possível carregar'}
          message={
            invalid
              ? 'Este formulário não existe ou foi desativado.'
              : `Tente novamente em instantes. (${errorMessage(view.error)})`
          }
        />
      </FormShell>
    );
  }

  if (sent) {
    return (
      <FormShell>
        <Notice
          icon={CheckCircle2}
          tone="success"
          title="Recebemos sua solicitação"
          message="Nossa equipe dará andamento e você será contatado pelo e-mail informado."
        />
      </FormShell>
    );
  }

  const textFields: Field[] = [
    { id: 'pf-name', label: 'Nome', value: name, set: setName },
    { id: 'pf-email', label: 'E-mail', value: email, set: setEmail },
    { id: 'pf-title', label: 'Título', value: title, set: setTitle },
  ];

  return (
    <FormShell>
      <div className="rounded-lg border bg-card p-6 shadow-xs">
        <h1 className="text-center text-base font-semibold text-foreground">
          Abrir solicitação — <span className="text-primary">{view.data.deskName}</span>
        </h1>
        <p className="mt-1 text-center text-sm text-muted-foreground">
          Preencha os dados abaixo e nossa equipe dará andamento.
        </p>

        <form className="mt-5 flex flex-col gap-4" onSubmit={submit} noValidate>
          {textFields.map((f) => (
            <div key={f.id} className="flex flex-col gap-1.5">
              <Label htmlFor={f.id} className="text-[13px] font-semibold">
                {f.label}
                <span className="-ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id={f.id}
                type={f.id === 'pf-email' ? 'email' : 'text'}
                value={f.value}
                onChange={(e) => f.set(e.target.value)}
                placeholder={
                  f.id === 'pf-name'
                    ? 'Seu nome'
                    : f.id === 'pf-email'
                      ? 'voce@empresa.com.br'
                      : 'Resumo da solicitação'
                }
                required
              />
            </div>
          ))}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="pf-description" className="text-[13px] font-semibold">
              Descrição<span className="-ml-0.5 text-destructive">*</span>
            </Label>
            <Textarea
              id="pf-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Detalhe o que você precisa…"
              className="min-h-28"
              required
            />
          </div>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="h-10 w-full" disabled={submitForm.isPending}>
            {submitForm.isPending ? (
              <Spinner className="size-4 border-primary-foreground" />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            Enviar solicitação
          </Button>
        </form>
      </div>
    </FormShell>
  );
}
